/**
 * FilterMultiSelect
 *
 * A filter-oriented multi-select built on Mantine's Combobox primitives.
 *
 * Features:
 * - Empty trigger: left chevron + placeholder (e.g. "Select Provider")
 * - Selected trigger: segmented [placeholder | summary | X]
 *   - 1 selection → option label; 2+ → "N Selected"
 * - Dropdown with search (always), checkbox options
 * - Summary updates immediately as checkboxes toggle
 *
 * Value semantics (default):
 * - [] means no selections — row filters should show no rows
 * - ['a', 'b'] means those items are selected
 *
 * Optional emptyMeansAll=true restores legacy “[] = all checked” checkbox behavior.
 */

import {
  Box,
  Checkbox,
  Combobox,
  Divider,
  Text,
  ScrollArea,
  useCombobox,
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface FilterMultiSelectOption {
  value: string;
  label: string;
  /** Shown in trigger when this option is the only selection; falls back to label if absent. */
  displayLabel?: string;
  disabled?: boolean;
}

export interface FilterMultiSelectSection {
  label: string;
  data: FilterMultiSelectData;
}

export type FilterMultiSelectData = ReadonlyArray<
  string | { value: string; label: string; displayLabel?: string; disabled?: boolean }
>;

export interface FilterMultiSelectProps {
  /** Options to choose from (do NOT include an "All" option — the component adds it when shown). */
  data: FilterMultiSelectData;
  /** Optional categorized sections; when set, renders grouped options instead of a flat list. */
  sections?: ReadonlyArray<FilterMultiSelectSection>;
  /** Label for the "All" row when shown, e.g. "All team members". Also used as placeholder fallback. */
  allLabel: string;
  /** Currently selected values. Empty array = no selections (unless emptyMeansAll). */
  value: string[];
  onChange: (value: string[]) => void;
  /** Empty-trigger and selected left-segment text (e.g. "Select Provider"). Falls back from allLabel. */
  placeholder?: string;
  /** Placeholder inside the dropdown search input. @default 'Search' */
  searchPlaceholder?: string;
  /**
   * Treat an empty value array as "all selected" in the checkbox list (legacy).
   * @default false — [] means no selections.
   */
  emptyMeansAll?: boolean;
  disabled?: boolean;
  /** Label above the trigger input. */
  label?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Icon to the left of the trigger (empty state only; ignored when selected). */
  leftSection?: React.ReactNode;
  /** Maximum height of the scrollable options area. @default 250 */
  maxDropdownHeight?: number;
  /** Wrapper width (e.g. w={280}) */
  w?: number | string;
  className?: string;
  /** Wrapper style (e.g. minWidth for dropdowns) */
  style?: React.CSSProperties;
  /** Noun for multi-select summary, e.g. "Selected" → "2 Selected". @default 'Selected' */
  selectedLabel?: string;
  /** Option values to render above the divider (e.g. "My Clients", "My Team"); rest render below. */
  optionsAboveDivider?: string[];
  /** Optional label shown above the options that appear above the divider (e.g. "Active"). */
  groupLabelAbove?: string;
  /** Optional label shown above the options that appear below the divider (e.g. "Finished"). */
  groupLabelBelow?: string;
  /** Show clear (X) segment when there is a selection. @default true */
  clearable?: boolean;
  /** Hide the top "All …" row. @default true */
  hideAllOption?: boolean;
  [key: string]: unknown;
}

function normalizeData(data: FilterMultiSelectData): FilterMultiSelectOption[] {
  return data.map((d) => (typeof d === 'string' ? { value: d, label: d } : d));
}

const ALL_KEY = '__filter_ms_all__';

const MIN_HEIGHT_BY_SIZE: Record<NonNullable<FilterMultiSelectProps['size']>, string> = {
  xs: '30px',
  sm: '36px',
  md: '42px',
  lg: '50px',
  xl: '60px',
};

/** Derive "Select Providers" from "All Providers" when placeholder is omitted. */
export function placeholderFromAllLabel(allLabel: string): string {
  return allLabel.replace(/^All\s+/i, 'Select ');
}

/**
 * Row-filter helper for FilterMultiSelect values. An empty selection matches nothing.
 */
export function matchesFilterSelection(
  selected: readonly string[],
  candidate: string | undefined | null,
): boolean {
  if (selected.length === 0) return false;
  if (candidate == null || candidate === '') return false;
  return selected.includes(candidate);
}

/**
 * Build the selected-state middle-segment text.
 * 1 item → its display label; 2+ → "N Selected" (or custom selectedLabel).
 */
export function formatSelectionSummary(
  selected: ReadonlyArray<{ label: string }>,
  selectedLabel = 'Selected',
): string {
  if (selected.length === 0) return '';
  if (selected.length === 1) return selected[0].label;
  return `${selected.length} ${selectedLabel}`;
}

const FMS_STYLES = `
.fms-dropdown [data-combobox-option] {
  overflow: hidden;
  word-break: normal;
}
.fms-dropdown .fms-row {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  width: 100%;
}
.fms-dropdown .fms-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: inherit;
  line-height: 1.55;
}
.fms-dropdown .mantine-ScrollArea-viewport {
  overflow-x: hidden !important;
}
.fms-trigger {
  display: inline-flex;
  align-items: stretch;
  width: 100%;
  min-height: var(--fms-min-height, 42px);
  box-sizing: border-box;
  border: 1px solid #9CA3AF;
  border-radius: var(--mantine-radius-md);
  background: var(--mantine-color-white);
  color: var(--mantine-color-dark-6);
  font-size: var(--mantine-font-size-sm);
  line-height: 1.4;
  cursor: pointer;
  overflow: hidden;
}
.fms-trigger[data-disabled] {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}
.fms-trigger--empty {
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  color: var(--mantine-color-gray-7);
  font: inherit;
  text-align: left;
}
.fms-trigger--empty .fms-chevron {
  display: flex;
  flex-shrink: 0;
  color: var(--mantine-color-dark-3);
}
.fms-trigger--empty .fms-empty-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.fms-seg {
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0 10px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  min-width: 0;
}
.fms-seg:hover {
  background: var(--mantine-color-gray-1);
}
.fms-seg-label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--mantine-color-gray-6);
  border-right: 1px solid #9CA3AF;
}
.fms-seg-label-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fms-seg-value {
  flex: 1;
  font-weight: 700;
  color: var(--mantine-color-dark-7);
  overflow: hidden;
}
.fms-seg-value-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fms-seg-clear {
  flex-shrink: 0;
  justify-content: center;
  padding: 0 8px;
  border-left: 1px solid #9CA3AF;
  color: var(--mantine-color-dark-3);
}
.fms-field-label {
  display: block;
  font-size: var(--mantine-font-size-sm);
  font-weight: 500;
  margin-bottom: 4px;
}
`;

export function FilterMultiSelect({
  data,
  sections,
  allLabel,
  value,
  onChange,
  placeholder,
  searchPlaceholder = 'Search',
  emptyMeansAll = false,
  disabled = false,
  label,
  size,
  leftSection,
  maxDropdownHeight = 250,
  style,
  className,
  w,
  selectedLabel = 'Selected',
  optionsAboveDivider,
  groupLabelAbove,
  groupLabelBelow,
  clearable = true,
  hideAllOption = true,
}: FilterMultiSelectProps): JSX.Element {
  const resolvedSize = size ?? 'md';
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
      setManualNone(false);
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const [search, setSearch] = useState('');

  // Tracks an explicit "none selected" state that can't be represented via
  // value=[] when emptyMeansAll is true.  Resets when the dropdown closes or
  // the parent supplies a non-empty value.
  const [manualNone, setManualNone] = useState(false);

  useEffect(() => {
    if (value.length > 0) setManualNone(false);
  }, [value]);

  const triggerPlaceholder = placeholder ?? placeholderFromAllLabel(allLabel);

  const options = useMemo(() => {
    if (sections && sections.length > 0) {
      return sections.flatMap((section) => normalizeData(section.data));
    }
    return normalizeData(data);
  }, [data, sections]);
  const optionValues = useMemo(() => options.map((o) => o.value), [options]);

  const allSelected =
    !manualNone &&
    ((emptyMeansAll && value.length === 0) ||
      (optionValues.length > 0 && value.length === optionValues.length));

  const isChecked = useCallback(
    (val: string): boolean =>
      !manualNone &&
      ((emptyMeansAll && value.length === 0) || value.includes(val)),
    [value, emptyMeansAll, manualNone],
  );

  const valueToOption = useMemo(
    () => new Map(options.map((o) => [o.value, o])),
    [options],
  );

  const selectedItems = useMemo(() => {
    // [] is always "no selections" in the trigger (even with emptyMeansAll — that only
    // affects checkbox visuals / collapsing a full selection back to []).
    if (manualNone || value.length === 0) return [];
    return value.map((v) => ({
      value: v,
      label: valueToOption.get(v)?.displayLabel ?? valueToOption.get(v)?.label ?? v,
    }));
  }, [manualNone, value, valueToOption]);

  const showSelectedTrigger = selectedItems.length > 0 || manualNone;
  const summaryText = formatSelectionSummary(selectedItems, selectedLabel);

  const filteredOptions = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, search]);

  const filteredSections = useMemo(() => {
    if (!sections || sections.length === 0) return null;
    const q = search.toLowerCase().trim();
    return sections
      .map((section) => ({
        label: section.label,
        options: normalizeData(section.data).filter(
          (o) => !q || o.label.toLowerCase().includes(q),
        ),
      }))
      .filter((section) => section.options.length > 0);
  }, [sections, search]);

  const aboveDividerValues = useMemo(
    () => new Set(optionsAboveDivider ?? []),
    [optionsAboveDivider],
  );
  const optionsAbove = useMemo(
    () => filteredOptions.filter((o) => aboveDividerValues.has(o.value)),
    [filteredOptions, aboveDividerValues],
  );
  const optionsBelow = useMemo(
    () => filteredOptions.filter((o) => !aboveDividerValues.has(o.value)),
    [filteredOptions, aboveDividerValues],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setManualNone(false);
      onChange([]);
    },
    [onChange],
  );

  const openDropdown = useCallback(() => {
    if (!disabled) combobox.toggleDropdown();
  }, [combobox, disabled]);

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openDropdown();
      }
    },
    [openDropdown],
  );

  const handleOptionSubmit = useCallback(
    (val: string) => {
      if (val === ALL_KEY) {
        if (allSelected) {
          if (emptyMeansAll) {
            setManualNone(true);
          } else {
            onChange([]);
          }
        } else {
          setManualNone(false);
          onChange(emptyMeansAll ? [] : [...optionValues]);
        }
        return;
      }

      if (manualNone) {
        setManualNone(false);
        onChange([val]);
        return;
      }

      const currentSet =
        emptyMeansAll && value.length === 0 ? [...optionValues] : [...value];

      const idx = currentSet.indexOf(val);
      if (idx >= 0) {
        currentSet.splice(idx, 1);
      } else {
        currentSet.push(val);
      }

      if (emptyMeansAll && currentSet.length === optionValues.length) {
        onChange([]);
      } else {
        onChange(currentSet);
      }
    },
    [value, onChange, optionValues, emptyMeansAll, allSelected, manualNone],
  );

  const renderOptionRow = (option: FilterMultiSelectOption) => (
    <Combobox.Option
      key={option.value}
      value={option.value}
      disabled={option.disabled}
    >
      <div className="fms-row">
        <Checkbox
          size="sm"
          color="gold"
          checked={isChecked(option.value)}
          readOnly
          tabIndex={-1}
          style={{ flexShrink: 0 }}
        />
        <span className="fms-label">{option.label}</span>
      </div>
    </Combobox.Option>
  );

  const showClear = clearable && showSelectedTrigger;

  const trigger = showSelectedTrigger ? (
    <div
      className="fms-trigger fms-trigger--selected"
      aria-haspopup="listbox"
      aria-expanded={combobox.dropdownOpened}
      data-disabled={disabled || undefined}
    >
      <button
        type="button"
        className="fms-seg fms-seg-label"
        disabled={disabled}
        aria-label={triggerPlaceholder}
        onClick={openDropdown}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="fms-seg-label-text">{triggerPlaceholder}</span>
      </button>
      <button
        type="button"
        className="fms-seg fms-seg-value"
        disabled={disabled}
        aria-label={`${triggerPlaceholder}: ${summaryText || 'none'}`}
        onClick={openDropdown}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="fms-seg-value-text">
          {manualNone ? 'None' : summaryText}
        </span>
      </button>
      {showClear && (
        <button
          type="button"
          className="fms-seg fms-seg-clear"
          disabled={disabled}
          aria-label="Clear selection"
          onClick={handleClear}
        >
          <IconX size={14} stroke={1.75} />
        </button>
      )}
    </div>
  ) : (
    <button
      type="button"
      className="fms-trigger fms-trigger--empty"
      tabIndex={disabled ? -1 : 0}
      aria-haspopup="listbox"
      aria-expanded={combobox.dropdownOpened}
      aria-label={triggerPlaceholder}
      disabled={disabled}
      data-disabled={disabled || undefined}
      onClick={openDropdown}
      onKeyDown={handleTriggerKeyDown}
    >
      <span className="fms-chevron" aria-hidden>
        {leftSection ?? <Combobox.Chevron />}
      </span>
      <span className="fms-empty-text">{triggerPlaceholder}</span>
    </button>
  );

  return (
    <Box
      w={w}
      className={className}
      style={{
        minWidth: 220,
        ...style,
        ['--fms-min-height' as string]: MIN_HEIGHT_BY_SIZE[resolvedSize],
      }}
    >
      <style>{FMS_STYLES}</style>
      {label != null && label !== false && (
        <span className="fms-field-label">{label}</span>
      )}
      <Combobox
        store={combobox}
        onOptionSubmit={handleOptionSubmit}
        size={resolvedSize}
        classNames={{ dropdown: 'fms-dropdown' }}
      >
        <Combobox.DropdownTarget>{trigger}</Combobox.DropdownTarget>

        <Combobox.Dropdown>
          <Combobox.Search
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder={searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            styles={{ input: { borderBottom: 'none', marginTop: 1 } }}
          />
          <Divider color="gray.3" />
          <ScrollArea.Autosize
            type="scroll"
            mah={maxDropdownHeight}
            scrollbars="y"
          >
            <Combobox.Options>
              {!hideAllOption && (
                <Combobox.Option value={ALL_KEY}>
                  <div className="fms-row">
                    <Checkbox
                      size="sm"
                      color="gold"
                      checked={allSelected}
                      readOnly
                      tabIndex={-1}
                      style={{ flexShrink: 0 }}
                    />
                    <span className="fms-label">{allLabel}</span>
                  </div>
                </Combobox.Option>
              )}

              {filteredSections ? (
                filteredSections.length === 0 ? (
                  <Combobox.Empty>No results found</Combobox.Empty>
                ) : (
                  filteredSections.map((section, index) => (
                    <div key={section.label}>
                      {(index > 0 || !hideAllOption) && <Divider my={4} />}
                      <Text
                        size="xs"
                        c="dimmed"
                        fw={600}
                        mb={4}
                        mt={2}
                        px="sm"
                      >
                        {section.label}
                      </Text>
                      {section.options.map(renderOptionRow)}
                    </div>
                  ))
                )
              ) : (
                <>
                  {!hideAllOption && (optionsAbove.length > 0 || optionsBelow.length > 0) && (
                    <Divider my={4} />
                  )}

                  {optionsAbove.length > 0 && (
                    <>
                      {groupLabelAbove && (
                        <Text
                          size="xs"
                          c="dimmed"
                          fw={600}
                          mb={4}
                          mt={2}
                          ta="center"
                        >
                          {groupLabelAbove}
                        </Text>
                      )}
                      {optionsAbove.map(renderOptionRow)}
                    </>
                  )}

                  {optionsAbove.length > 0 && optionsBelow.length > 0 && (
                    <Divider my={4} />
                  )}

                  {optionsBelow.length === 0 && optionsAbove.length === 0 ? (
                    <Combobox.Empty>No results found</Combobox.Empty>
                  ) : optionsBelow.length === 0 ? null : (
                    <>
                      {groupLabelBelow && (
                        <Text
                          size="xs"
                          c="dimmed"
                          fw={600}
                          mb={4}
                          mt={2}
                          ta="center"
                        >
                          {groupLabelBelow}
                        </Text>
                      )}
                      {optionsBelow.map(renderOptionRow)}
                    </>
                  )}
                </>
              )}
            </Combobox.Options>
          </ScrollArea.Autosize>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
}
